const WRAPPED_CSS_TEXT = `
      .gift-block__launcher {
        padding-left: 16px !important;
        padding-right: 16px !important;
        border-radius: 1rem !important;
      }
      .gift-block__launcher:hover, 
      gift-dialog-actions button:hover {
        background-color: #006375 !important;
        color: #CFEBED !important;
      }
      .gift-block__launcher .label{
        font-size: 14px !important;
        line-height: 20px !important;
      }
      gift-dialog-header #close-button, 
      gift-dialog-header #close-button svg {
        color: #00b0be !important;
        background: transparent;
      }
      gift-dialog-header #close-button > span {
        font-size: 24px;
      }
      gift-dialog-content .gift-product-header,
      gift-dialog-actions:has(#remove-button:not([hidden]):not(.hidden):not([style*="display:none"])) > #save-button {
        display: none !important;
      }
      gift-dialog-actions button {
        border: 1px solid #00b0be !important;
        background: transparent !important;
      }
      /* Hide Storefront Web Components fetch errors (e.g. Safari Load failed) -- SG 20260929 */
      .shopify-error {
        display: none !important;
      }
  `;

// Scoped to the nested actions shadow root, where buttons are direct siblings -- SG 20261005
const WRAPPED_ACTIONS_CSS_TEXT = `
      #save-button:has(~ #remove-button:not([hidden]):not(.hidden):not([style*="display:none"])) {
        display: none !important;
      }
  `;

const SAVE_BUTTON_LABEL_SELECTOR = "#save-button > .button-content > .label:not(.out-of-stock)";
const SAVE_BUTTON_LABEL_TEXT = "Add Gift Box to Order";

const observedRoots = new WeakSet<ShadowRoot>();
let bodyObserverRegistered = false;

// Wrapped renders dialog actions inside a nested gift-block-actions shadow root -- SG 20261005
const getActionRoots = (root: ShadowRoot): ShadowRoot[] => {
  const roots = [root];
  root.querySelectorAll("gift-block-actions, gift-dialog-actions").forEach((actions) => {
    if (actions.shadowRoot) roots.push(actions.shadowRoot);
  });
  return roots;
};

// Edit the existing text node so Wrapped keeps ownership of the label element -- SG 20261005
const setLabelText = (label: Element, text: string): void => {
  if (label.textContent?.trim() === text) return;

  const onlyChild = label.childNodes.length === 1 ? label.firstChild : null;
  if (onlyChild && onlyChild.nodeType === Node.TEXT_NODE) {
    (onlyChild as Text).data = text;
  } else {
    label.textContent = text;
  }
};

// Prevent multiple style injections into the same root
const injectStyle = (root: ShadowRoot, key: string, cssText: string): void => {
  if (root.querySelector(`style[data-custom-style="${key}"]`)) return;

  const newStyle = document.createElement("style");
  newStyle.setAttribute("data-custom-style", key);
  newStyle.textContent = cssText;
  root.appendChild(newStyle);
};

const styleWrappedBlock = (elem: Element): void => {
  if (!elem.shadowRoot) return;

  // 1. Inject brand overrides into the gift block shadow root
  injectStyle(elem.shadowRoot, "wrapped-gift-block", WRAPPED_CSS_TEXT);

  // 2. Style and relabel action buttons, and watch nested action roots for re-renders
  getActionRoots(elem.shadowRoot).forEach((root) => {
    if (root !== elem.shadowRoot) injectStyle(root, "wrapped-gift-block-actions", WRAPPED_ACTIONS_CSS_TEXT);

    const saveButtonLabel = root.querySelector(SAVE_BUTTON_LABEL_SELECTOR);
    if (saveButtonLabel) setLabelText(saveButtonLabel, SAVE_BUTTON_LABEL_TEXT);
    observeRoot(root, elem);
  });
};

// characterData catches Wrapped updating label text in place -- SG 20261005
const observeRoot = (root: ShadowRoot, elem: Element): void => {
  if (observedRoots.has(root)) return;

  observedRoots.add(root);
  const shadowObserver = new MutationObserver(() => {
    styleWrappedBlock(elem);
  });
  shadowObserver.observe(root, { childList: true, subtree: true, characterData: true });
};

const processWrappedBlocks = (): void => {
  document.querySelectorAll("wrapped-gift-block").forEach((elem) => {
    styleWrappedBlock(elem);
  });
};

const nodeContainsWrappedBlock = (node: Node): boolean => {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;

  const element = node as Element;
  if (element.localName === "wrapped-gift-block") return true;

  return element.querySelectorAll("wrapped-gift-block").length > 0;
};

const registerBodyObserver = (): void => {
  if (bodyObserverRegistered || !document.body) return;

  bodyObserverRegistered = true;
  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (let i = 0; i < mutation.addedNodes.length; i++) {
        if (nodeContainsWrappedBlock(mutation.addedNodes[i])) {
          processWrappedBlocks();
          return;
        }
      }
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
};

export const ApplyWrappedStyles = (): void => {
  processWrappedBlocks();
  setTimeout(processWrappedBlocks, 1500);

  customElements.whenDefined("wrapped-gift-block").then(() => {
    processWrappedBlocks();
  });

  registerBodyObserver();
};
